import { getFilterCacheList, getObjectByCache } from '@/utils/basicDataCache'
import { basicCacheKeys } from '@/utils/cacheConfig'
import { Assert } from '@/utils/validate'
import * as NumberUtils from '@/utils/NumberUtils'
import lang from '@/lang'
import { Job } from '@/class/model/booking/Job'
import { cache, cacheName } from '@/utils/cacheManager'
import * as RatePeriodUtil from '@/class/cargo/model/utils/RatePeriodUtil'
import $store from '@/store'
import { CurrencyUtil } from '@/class/cargo/model/utils/CurrencyUtil'
import store from '@/store'
import { Office } from '@/class/model/sys/Office'

// CARGO-38632 当业务日期跨年时，创建费用，外币费用的汇率取值错误
// export const MONTH_RANGE = 18
export const STANDARD_COIN = 'RMB'

/**
 * 获取汇率，如果指定日期没有汇率，那么就取指定日期上一个月的汇率
 * @param sourceCode 原币种
 * @param targetCode 目标币种
 * @param businessDate 汇率日期
 * @param branchId 分公司Id
 * @param lookup 循环查询次数 (默认:0)
 * @param warning 如果找不到汇率是否弹出警告框(默认：否)
 */
export function getForeignExchangeRateSafely(sourceCode, targetCode, businessDate, branchId = 0, lookup = 0, warning = false, nonRecursive = false) {
  if (!sourceCode || !targetCode) return 0
  if (sourceCode && targetCode && sourceCode === targetCode) return 1
  let monthRange = store.state.settings.rateCacheRange
  if (nonRecursive) {
    monthRange = 0
  }
  // CARGO-22900，汇率支持维护生效日期
  if (lookup === 0 && parseInt(cache.config.get('usePeriodRate')) === 1) {
    if (!branchId) { // 默认当前分公司
      branchId = $store.getters.currentOfficeId
    }
    const rate = RatePeriodUtil.getForeignExchangeRateSafely(sourceCode, targetCode, businessDate, branchId)
    if (!isNaN(rate)) return rate
  }
  // 判断日期是否为字符串（有时候后端返回前端是json数据,日期已经是字符串了,此时需要转换一下）
  if (typeof businessDate === 'string') {
    businessDate = new Date(businessDate.replace(/-/g, '/'))
  }
  // 如果指定日期走过12个月，那么汇率取当前月的 Marco:2015-11-17
  if (businessDate && getBetweenMonths(businessDate, new Date()) > monthRange) {
    businessDate = new Date()
  }
  const inputMonth = businessDate.getMonth() + 1
  const month = String(businessDate.getFullYear()) + (inputMonth >= 10 ? String(inputMonth) : '0' + String(inputMonth))
  const currentMonthRateArray = getFilterCacheList(basicCacheKeys.basic_data_FxRate, (item) => {
    return item.month === month
  })
  let rate = NaN
  if (currentMonthRateArray && currentMonthRateArray.length > 0) {
    rate = getRateInMonth(sourceCode, targetCode, currentMonthRateArray)
  }
  // 获取汇率，如果指定日期没有汇率，那么就取指定日期上一个月的汇率
  if (isNaN(rate) && lookup < monthRange && businessDate) {
    const lastMonth = getPreMonth(businessDate) // new Date(businessDate.getFullYear(), businessDate.getMonth(), businessDate.getDate())
    rate = getForeignExchangeRateSafely(sourceCode, targetCode, lastMonth, branchId, ++lookup, warning)
  } else if (isNaN(rate) && lookup >= monthRange && warning) {
    Assert.isTrue(false, lang.t('baseInfo.rateManagement.notFoundRate', [targetCode]))
  }
  return NumberUtils.roundHalfUp(rate, 8)
}

/**
 * 获取上一个月
 */
export function getPreMonth(date) {
  const year = date.getFullYear() // 获取年份
  const month = date.getMonth() // 获取月份
  let year2 = year
  let month2 = parseInt(month) - 1
  if (month2 === 0) {
    year2 = parseInt(year2) - 1
    month2 = 12
  }
  const days2 = new Date(year2, month2, 1)
  return days2
}

/**
 * 根据月份取汇率，如果连续最近6个月都没有，那么就去后台取。
 *
 * @param sourceCode
 * @param targetCode
 * @param date
 * @param branchId 分公司Id
 * @param callBack
 * @return
 *
 */
export async function getForeignExchangeRateByDate(sourceCode, targetCode, date, branchId = 0, callBack = null, warning = false) {
  const rate = await getForeignExchangeRateSafely(sourceCode, targetCode, date, branchId, 0, warning)
  if (rate === 0 && callBack != null) {
    // todo 调用后台方法
  }
  return rate
}

/**
 * 计算二个日期相差的天数
 */
export function getBetweenMonths(startDate, endDate) {
  return (endDate.getFullYear() - startDate.getFullYear()) * 12 + endDate.getMonth() - startDate.getMonth()
}

export function getRateInMonth(targetCode, sourceCode, rates) {
  if (targetCode === sourceCode) {
    return 1.0
  }

  for (let i = 0; i < rates.length; i++) {
    const rate = rates[i]
    if (rate.currency === sourceCode && rate.baseCuurency === targetCode) {
      return parseFloat(rate.rate)
    }
  }
  if (useLocalCurrency()) {
    for (let i = 0; i < rates.length; i++) {
      const rate = rates[i]
      if (rate.currency === targetCode && rate.baseCuurency === sourceCode) {
        return CurrencyUtil.roundHalfUpRateByCurrency(sourceCode, 1.0 / parseFloat(rate.rate))
      }
    }
  }

  let targetRate = null
  let sourceRate = null

  for (let i = 0; i < rates.length; i++) {
    const rate = rates[i]
    if (rate.currency === sourceCode) {
      targetRate = rate
      if (rate.baseCuurency === targetCode) {
        return parseFloat(targetRate.rate)
      }
      break
    }
  }

  for (let i = 0; i < rates.length; i++) {
    const rate = rates[i]
    if (rate.currency === targetCode) {
      sourceRate = rate
      if (rate.baseCuurency === sourceCode) {
        return CurrencyUtil.roundHalfUpRateByCurrency(sourceCode, 1.0 / parseFloat(sourceRate.rate))
      }
      break
    }
  }

  if (targetRate == null) {
    return NaN
  }

  if (sourceRate == null) {
    return NaN
  }

  if (sourceRate.baseCuurency !== targetRate.baseCuurency) {
    return NaN
  }
  const derived = parseFloat(targetRate.rate) / parseFloat(sourceRate.rate)
  return CurrencyUtil.roundHalfUpRateByCurrency(sourceCode, derived)
}

/**
 * 刷新Job下所有的费用汇率，包括小票汇率
 *
 * @param job
 * @param filingCurrency
 * @param callback
 * @param action 从哪个界面发起的动作
 */
export async function refreshChargeRatesByJob(job, filingCurrency = 'RMB', callback = null, action = '') {
  // CARGO-18269 当开启“锁定费用汇率节点”参数时，【更新汇率】不更新已关联发票的费用
  const invoiceSyncRate = parseInt(cache.config.get('invoiceSyncRate')) === 1
  const modifyCharges = []
  const charges = []
  let charge = {}
  if (job && (!job.charges || job.charges.length <= 0)) {
    await new Job(job).getCharges()
  }
  for (const charge of job.charges) {
    if (charge.currency !== filingCurrency) {
      charges.push(charge)
    }
  }

  for (let i = 0; i < charges.length; i++) {
    charge = charges[i]
    const localCurrency = await localCurrencyByOfficeId(charge.branchId)
    const exchangeRate = await getForeignExchangeRateByJob(localCurrency, charge.currency, job, charge.branchId, async function(rate) {
      await refreshChargeRatesByJob(job, filingCurrency, callback, action)
      if (callback != null) { callback() }
    })
    if (isNaN(exchangeRate)) {
      break
    } else {
      if (action === 'tabCharge') {
        if (!invoiceSyncRate || (invoiceSyncRate && !charge.invoiceUuid)) {
          charge.exchangeRate = exchangeRate
          modifyCharges.push(charge)
        }
      } else {
        charge.exchangeRate = exchangeRate
        modifyCharges.push(charge)
      }
    }
  }

  if (job.masterFlag) {
    for (const slave of job.lclSlaves) {
      await refreshChargeRatesByJob(slave, filingCurrency, null, action)
    }
  }
  return modifyCharges
}

/**
 * 根据Job取汇率，这里主要是自动判断ETA,ETA
 * 如果日期范围过大，要从后台取，那么会把从后台取的这个汇率缓存到前台<code>rateCache</code>里面
 * @param sourceCode
 * @param targetCode
 * @param job
 * @param branchId 分公司Id
 */
export async function getForeignExchangeRateByJob(sourceCode, targetCode, job, branchId = 0, callBack = null) {
  if (sourceCode === targetCode) { return 1 }
  let businessDate = null
  if (job && job.lob) {
    if (job.lob === 'SE' || job.lob === 'AE' || job.lob === 'RE' || job.lob === 'RI') {
      businessDate = job.etd
    } else if (job.lob === 'SI' || job.lob === 'AI') {
      businessDate = job.eta
    } else if (job.lob === 'IE') {
      businessDate = job.businessDate
    }
  }
  if (!businessDate) { businessDate = new Date() }
  return await getForeignExchangeRateByDate(sourceCode, targetCode, businessDate, branchId, callBack, true)
}

// 本位币是否按分公司币种
export function useLocalCurrency() {
  return cache.config && parseInt(cache.config.get('UseLocalCurrency')) === 1
}

// 分公司币种
export function localCurrencyByOffice() {
  const currency = STANDARD_COIN
  if (!useLocalCurrency()) return currency
  const office = $store.getters.currentOffice
  // console.log('localCurrency: ', office)
  if (office && office.currency) return office.currency
  return currency
}

// 分公司币种
export function localCurrencyByOffice2() {
  const currency = STANDARD_COIN
  const office = $store.getters.currentOffice
  if (office && office.currency) return office.currency
  return currency
}

export function localCurrencyByOfficeId(branchId) {
  const currency = STANDARD_COIN
  // CARGO-38344录入费用时业务汇率带入错误
  const message = 'branchId = ' + branchId + ';useLocalCurrency=' + !useLocalCurrency()
  if ($store && $store.getters && $store.getters.tenantId === 13145) {
    $store.dispatch('log/addLog', {
      type: 'info',
      msg: 'localCurrencyByOfficeId:' + message,
      url: window.location.href
    })
  }
  if (!branchId || !useLocalCurrency()) return currency
  const office = getObjectByCache(basicCacheKeys.basic_data_Office_All, 'officeId', branchId)
  // CARGO-38344录入费用时业务汇率带入错误
  if ($store && $store.getters && $store.getters.tenantId === 13145) {
    let message = 'office='
    if (!office) {
      message = message + 'null'
    } else {
      message = message + office.uuid + ';version=' + office.version + ';currency=' + office.currency
    }
    $store.dispatch('log/addLog', {
      type: 'info',
      msg: 'localCurrencyByOfficeId-office:' + message,
      url: window.location.href
    })
  }
  if (office && office.currency) return office.currency
  return currency
}

// 金额是否开启进位规则
export function useAmountCarryRule() {
  return cache.config && parseInt(cache.config.get('useAmountCarryRule')) === 1
}

// 金额根据小数位数、进位规则
export function getAmountByCurrency(amount, code) {
  if (!code || !useAmountCarryRule()) return NumberUtils.ToFixed2(amount, 2)
  try {
    let scale = 2 // 小数位数
    let carryRule = 0 // 进位规则：0=四合五入、1=舍位
    const currencyData = getCurrency(code)
    if (currencyData !== null) {
      scale = currencyData.defaultFractionDigits
      carryRule = currencyData.carryRule
    }
    if (carryRule === 1) { // 1=舍位
      return Number(NumberUtils.formatNumber(amount, scale, false))
    } else {
      return NumberUtils.ToFixed2(amount, scale)
    }
  } catch (e) {
    return amount
  }
}

// 获取币种
export function getCurrency(code) {
  // tenantId + sessionId + currencyCode
  const cacheKey = $store.getters.tenantId + $store.getters.token + '.' + code
  const currencyStr = sessionStorage.getItem(cacheKey)
  let currencyData = currencyStr ? JSON.parse(currencyStr) : null
  if (currencyData !== null) return currencyData
  //
  currencyData = getObjectByCache(basicCacheKeys.basic_data_Currency, 'code', code)
  if (currencyData !== null) {
    const currencyNew = {}
    currencyNew.defaultFractionDigits = currencyData.defaultFractionDigits
    currencyNew.carryRule = currencyData.carryRule
    sessionStorage.setItem(cacheKey, JSON.stringify(currencyNew))
  }
  return currencyData
}

// 是否开启接单中心模式
export function useOrderCenterMode() {
  return cache.config && parseInt(cache.config.get('useOrderCenterMode')) === 1
}

// 异步方法获取本位币
export async function localCurrencyByOfficeIdSync(branchId) {
  const currency = STANDARD_COIN
  const useLocalCurrency = await useLocalCurrencySync()
  if (!branchId || !useLocalCurrency) return currency
  let office = getObjectByCache(basicCacheKeys.basic_data_Office_All, 'officeId', branchId)
  if (!office) {
    office = await Office.getOfficeById(branchId)
  }
  if (office && office.currency) return office.currency
  return currency
}

// 异步方法获取配置
export async function useLocalCurrencySync() {
  await cache.load(cacheName.CONFIG)
  return cache.config && parseInt(cache.config.get('UseLocalCurrency')) === 1
}

// 获取HQ币种
export function getHqCurrency(branchId) {
  let currency = ''
  if (!branchId || !useLocalCurrency()) return currency
  // 分公司
  let office = getObjectByCache(basicCacheKeys.basic_data_Office_All, 'officeId', branchId)
  if (office && office.hqCurrency) currency = office.hqCurrency
  // HQ当地币种
  if (!currency && branchId > 1) {
    office = getObjectByCache(basicCacheKeys.basic_data_Office_All, 'officeId', 1)
    console.log('getHqCurrency: ', office)
    if (office && office.currency) return office.currency
  }
  // 未配置【HQ当地币种】
  currency = localCurrencyByOfficeId(branchId)
  return currency
}
